export const prompts = {
    singleAthleteTrainingSummary: data => {
        const weeks = data.weeklyAggregates; // Use all weeks
        const today = new Date();
        const currentWeekStart = new Date(today);
        currentWeekStart.setDate(today.getDate() - today.getDay() + (today.getDay() === 0 ? -6 : 1));
        const currentWeekKey = currentWeekStart.toISOString().split('T')[0];
        
        return `Analyze athlete training data for ${data.athleteName || 'Unknown'} (${data.period || `${weeks.length} weeks`}).

Weekly data with week-over-week changes (sessions, time, avg HR, max HR, training load metrics, ETS ratio, volume by sport):
${weeks.map((w, idx) => {
    const isCurrentWeek = w.week === currentWeekKey;
    const weekLabel = isCurrentWeek ? `W${w.week} (PARTIAL)` : `W${w.week}`;
    const trend = data.trends.weeklyChanges[idx];
    
    // Format volume by sport
    const sportVolumes = Object.entries(w.volumeBySport || {})
        .map(([sport, minutes]) => `${sport}:${minutes}min`)
        .join(', ');
    
    // Format sport volume changes (only include sports with valid percentage changes)
    const sportChanges = trend && idx > 0 && Object.keys(trend.volumeBySportChange || {}).length > 0
        ? Object.entries(trend.volumeBySportChange)
            .filter(([sport, change]) => change !== null)
            .map(([sport, change]) => `${sport}${change > 0 ? '+' : ''}${change}%`)
        : [];
    const sportChangesStr = sportChanges.length > 0 ? ', Sport changes: ' + sportChanges.join(', ') : '';
    
    // Format ETS ratio zone change
    const etsRatioInfo = w.etsRatio !== null ? `ETS-Ratio:${w.etsRatio} (${w.etsRatioZone})` : 'ETS-Ratio:N/A';
    const etsRatioChangeStr = trend && trend.etsRatioZoneChange ? ` → ${trend.etsRatioZoneChange}` : '';
    
    const changes = trend && idx > 0 
        ? ` | Changes vs prev week: Vol${trend.volumeChange > 0 ? '+' : ''}${trend.volumeChange}%, ETS${trend.etsChange > 0 ? '+' : ''}${trend.etsChange}%, TRIMP${trend.trimpChange > 0 ? '+' : ''}${trend.trimpChange}%, Foster${trend.fosterChange > 0 ? '+' : ''}${trend.fosterChange}%, AvgHR${trend.avgHrChange > 0 ? '+' : ''}${trend.avgHrChange}%, MaxHR${trend.maxHrChange > 0 ? '+' : ''}${trend.maxHrChange}%${sportChangesStr}`
        : '';
    
    return `${weekLabel}: ${w.sessions}sess, ${w.totalTimeMinutes}min, AvgHR:${w.avgHeartRate || 'N/A'}, MaxHR:${w.maxHeartRate || 'N/A'}, Load(ETS:${w.ets || 0}, TRIMP:${w.trimp || 0}, Foster:${w.foster || 0}), ${etsRatioInfo}${etsRatioChangeStr}, Sports(${sportVolumes || 'None'})${changes}`;
}).join('\n')}

Note: ETS, TRIMP, and Foster are training load/stress metrics (not heart rate). Partial weeks should not be compared directly to complete weeks. Week-over-week changes show progression patterns.

ETS Ratio Explanation:
- ETS Ratio = 7-day ETS / 28-day ETS (acute load / chronic load)
- De-training (<0.8): Training load is too low relative to recent baseline, athlete may be losing fitness
- Optimum (0.8-1.2): Ideal balance between training stress and recovery
- Tough Training (1.2-1.5): High acute load, monitor for fatigue and ensure adequate recovery
- Danger Zone (≥1.5): Very high acute load relative to chronic baseline, significant overtraining risk
- Use ETS Ratio as the PRIMARY indicator of overall training load trend and athlete readiness
- Zone changes (e.g., Optimum → Tough Training) are more significant than small percentage changes in individual metrics

Guidelines:
- Write in clear, simple English.
- Use exact numbers, percentages, and dates where possible.
- Be honest about missing or unreliable data.
- Do not invent information.
- Do not make medical claims.
- Always use calendar week number / year in the response.
- Do not diagnose injury risk, but you may identify possible training-load risks.
- Keep tone constructive and performance-focused.
- BE CONCISE: Use minimal words. Avoid filler phrases like "it appears", "it seems", "we can see".
- State facts directly. Add brief reasoning only when it clarifies a concern or recommendation.
- BREVITY: Each strength/concern/recommendation should be 8-15 words. Use complete, clear statements.
- Cite specific numbers and percentages to support observations (e.g., "Volume increased 25% over 3 weeks").
- NO REPETITION: Don't restate data already in the headline or summary.
- NO EXAGGERATION: Use measured language. Avoid words like "dramatic", "massive", "extreme" unless data truly warrants it.
- Be specific: Instead of "high volume", say "180min/week" or "40% above baseline".

Sport Distribution Analysis:
- Analyze the balance and variety of sports across weeks
- Identify if the athlete is focusing too heavily on one sport (>80% of total volume)
- Note sudden drops or complete absence of a previously regular sport
- Flag excessive sport-switching or lack of consistency in primary sport
- Consider if cross-training balance is appropriate (e.g., 70-80% primary sport, 20-30% complementary)
- Highlight weeks with no sport variety if athlete typically does multiple sports
- Recognize positive patterns like consistent cross-training or strategic sport rotation

Training Load Analysis Priority:
- Prioritize ETS Ratio and zone changes as the most important training load indicator
- Use ETS Ratio trends to assess if training progression is appropriate
- Correlate ETS Ratio zones with heart rate trends and volume changes for comprehensive assessment
- Flag concerns when ETS Ratio enters Danger Zone or shows rapid zone changes
- Recognize when athlete maintains Optimum zone consistently as a strength

Provide JSON:
{"headline":"one sentence","summary":"2-3 sentences","strengths":["3 items"],"concerns":["2 items"],"recommendations":["3 items"],"message":"motivational note"}

JSON only:`;
    }
};

export default prompts;
